/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DevoirStudent } from "./devoirStudent.type";
import { Devoir } from "../devoir/devoir.type";



export const DevoirStudentApi = createApi({
    reducerPath: "devoirStudentApi",
    tagTypes: ["devoirStudent", "devoirStudents", 'devoirStudentsByUser'],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditDevoirStudent: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "devoirStudents"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Devoir ajouté.",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "devoirStudents", id);
                        await updateDoc(docRef, {
                            ...data,
                            updatedAt: serverTimestamp(),
                        });

                        return {
                            data: {
                                message:
                                    "Devoir modifié !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["devoirStudent", "devoirStudents"],
        }),
        getDevoirStudents: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "devoirStudents"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const devoirStudents = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: devoirStudents as DevoirStudent[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const devoirStudentsRef = query(
                    collection(db, "devoirStudents"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(devoirStudentsRef, (snapshot) => {
                        const devoirStudents = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => devoirStudents as DevoirStudent[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["devoirStudents"],
        }),
        getDevoirStudentByProf: builder.query({
            async queryFn(studentId: string) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }

                    const userId = user.uid;

                    const q = query(
                        collection(db, "devoirStudents"),
                        where("createdBy", "==", userId),
                        where('studentId', "==", studentId),
                        orderBy("createdAt", "desc")
                    );

                    return new Promise((resolve) => {
                        onSnapshot(q, async (snapshot) => {

                            const devoirStudents = await Promise.all(
                                snapshot.docs.map(async (devoirStudent) => {
                                    const data = devoirStudent.data();

                                    // Récupérer devoir
                                    let devoirData = null;
                                    if (data.devoirId) {
                                        const devoirSnap = await getDoc(
                                            doc(db, "devoirs", data.devoirId)
                                        );
                                        if (devoirSnap.exists()) {
                                            devoirData = {
                                                id: devoirSnap.id,
                                                ...devoirSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer matiere
                                    let matiereData = null;
                                    if (data.matiereId) {
                                        const matiereSnap = await getDoc(
                                            doc(db, "matieres", data.matiereId)
                                        );
                                        if (matiereSnap.exists()) {
                                            matiereData = {
                                                id: matiereSnap.id,
                                                ...matiereSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer student
                                    let studentData = null;
                                    if (data.studentId) {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", data.studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            studentData = {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                    }

                                    return {
                                        id: devoirStudent.id,
                                        ...data,
                                        devoirData,
                                        studentData,
                                    };
                                })
                            );

                            resolve({ data: devoirStudents });
                        });
                    });

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const DevoirStudentsRef = query(
                    collection(db, "devoirStudents"),
                    where("createdBy", "==", auth.currentUser?.uid),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(DevoirStudentsRef, async (snapshot) => {

                        const devoirStudents = await Promise.all(
                            snapshot.docs.map(async (devoirStudent) => {
                                const data = devoirStudent.data();

                                // Récupérer devoir
                                let devoirData = null;
                                if (data.devoirId) {
                                    const devoirSnap = await getDoc(
                                        doc(db, "devoirs", data.devoirId)
                                    );
                                    if (devoirSnap.exists()) {
                                        devoirData = {
                                            id: devoirSnap.id,
                                            ...devoirSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer matiere
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(
                                        doc(db, "matieres", data.matiereId)
                                    );
                                    if (matiereSnap.exists()) {
                                        matiereData = {
                                            id: matiereSnap.id,
                                            ...matiereSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer student
                                let studentData = null;
                                if (data.studentId) {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", data.studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        studentData = {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                }

                                return {
                                    id: devoirStudent.id,
                                    ...data,
                                    devoirData,
                                    studentData,
                                    matiereData,
                                };
                            })
                        );

                        updateCachedData(() => devoirStudents);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["devoirStudents"],
        }),
        getDevoirStudentById: builder.query({
            queryFn: async (devoirStudentId) => {
                try {
                    const DevoirStudentRef = doc(db, "devoirStudents", devoirStudentId);
                    const DevoirStudentSnap = await getDoc(DevoirStudentRef);

                    if (!DevoirStudentSnap.exists()) {
                        return {
                            error: { status: 404, message: "Devoir Student introuvable !" },
                        };
                    }

                    return { data: { id: DevoirStudentSnap.id, ...DevoirStudentSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["devoirStudent"],
        }),
        // Supprimer
        deleteDevoirStudent: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "devoirStudents", uid);

                    // 1. Récupérer le document avant suppression
                    const docSnap = await getDoc(docRef);
                    const devoirStudent = docSnap.data() as DevoirStudent;
                    const { devoirId, studentId } = devoirStudent;

                    // 2. Supprimer devoirStudent
                    await deleteDoc(docRef);

                    // 3. Mettre à jour devoir lié
                    if (devoirId) {
                        const devoirRef = doc(db, "devoirs", devoirId);
                        const devoirSnap = await getDoc(devoirRef);
                        const devoirData = devoirSnap.data() as Devoir;

                        // Retirer le student de la liste
                        const updatedStudents = devoirData.students.filter(
                            (id) => id !== studentId
                        );

                        await updateDoc(devoirRef, {
                            students: updatedStudents,
                            updatedAt: serverTimestamp(),
                        });
                    }

                    return {
                        data: { uid, message: "Devoir supprimé.", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["devoirStudent", "devoirStudents"],
        }),
        getDevoirStudentsByUser: builder.query({
            async queryFn(id?: string) {
                try {
                    if (!id) {
                        return { data: [] as DevoirStudent[] };
                    }

                    const userId = id;

                    const q = query(
                        collection(db, "devoirStudents"),
                        where('studentId', "==", userId),
                        orderBy("createdAt", "desc")
                    );

                    return new Promise((resolve) => {
                        onSnapshot(q, async (snapshot) => {

                            const devoirStudents = await Promise.all(
                                snapshot.docs.map(async (devoirStudent) => {
                                    const data = devoirStudent.data();

                                    // Récupérer devoir
                                    let devoirData = null;
                                    if (data.devoirId) {
                                        const devoirSnap = await getDoc(
                                            doc(db, "devoirs", data.devoirId)
                                        );
                                        if (devoirSnap.exists()) {
                                            devoirData = {
                                                id: devoirSnap.id,
                                                ...devoirSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer matiere
                                    let matiereData = null;
                                    if (data.matiereId) {
                                        const matiereSnap = await getDoc(
                                            doc(db, "matieres", data.matiereId)
                                        );
                                        if (matiereSnap.exists()) {
                                            matiereData = {
                                                id: matiereSnap.id,
                                                ...matiereSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer student
                                    let studentData = null;
                                    if (data.studentId) {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", data.studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            studentData = {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                    }

                                    return {
                                        id: devoirStudent.id,
                                        ...data,
                                        devoirData,
                                        studentData,
                                    };
                                })
                            );

                            resolve({ data: devoirStudents });
                        });
                    });

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                if (!arg) {
                    return;
                }

                const DevoirStudentsRef = query(
                    collection(db, "devoirStudents"),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(DevoirStudentsRef, async (snapshot) => {

                        const devoirStudents = await Promise.all(
                            snapshot.docs.map(async (devoirStudent) => {
                                const data = devoirStudent.data();

                                // Récupérer devoir
                                let devoirData = null;
                                if (data.devoirId) {
                                    const devoirSnap = await getDoc(
                                        doc(db, "devoirs", data.devoirId)
                                    );
                                    if (devoirSnap.exists()) {
                                        devoirData = {
                                            id: devoirSnap.id,
                                            ...devoirSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer matiere
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(
                                        doc(db, "matieres", data.matiereId)
                                    );
                                    if (matiereSnap.exists()) {
                                        matiereData = {
                                            id: matiereSnap.id,
                                            ...matiereSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer student
                                let studentData = null;
                                if (data.studentId) {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", data.studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        studentData = {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                }

                                return {
                                    id: devoirStudent.id,
                                    ...data,
                                    devoirData,
                                    studentData,
                                    matiereData,
                                };
                            })
                        );

                        updateCachedData(() => devoirStudents);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["devoirStudentsByUser"],
        }),
        getDevoirStudentsByDevoir: builder.query({
            async queryFn(devoirId: string) {
                try {


                    const q = query(
                        collection(db, "devoirStudents"),
                        where('devoirId', "==", devoirId),
                        orderBy("createdAt", "desc")
                    );

                    return new Promise((resolve) => {
                        onSnapshot(q, async (snapshot) => {

                            const devoirStudents = await Promise.all(
                                snapshot.docs.map(async (devoirStudent) => {
                                    const data = devoirStudent.data();

                                    // Récupérer devoir
                                    let devoirData = null;
                                    if (data.devoirId) {
                                        const devoirSnap = await getDoc(
                                            doc(db, "devoirs", data.devoirId)
                                        );
                                        if (devoirSnap.exists()) {
                                            devoirData = {
                                                id: devoirSnap.id,
                                                ...devoirSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer matiere
                                    let matiereData = null;
                                    if (data.matiereId) {
                                        const matiereSnap = await getDoc(
                                            doc(db, "matieres", data.matiereId)
                                        );
                                        if (matiereSnap.exists()) {
                                            matiereData = {
                                                id: matiereSnap.id,
                                                ...matiereSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer student
                                    let studentData = null;
                                    if (data.studentId) {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", data.studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            studentData = {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                    }

                                    return {
                                        id: devoirStudent.id,
                                        ...data,
                                        devoirData,
                                        studentData,
                                    };
                                })
                            );

                            resolve({ data: devoirStudents });
                        });
                    });

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const DevoirStudentsRef = query(
                    collection(db, "devoirStudents"),
                    where("devoirId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(DevoirStudentsRef, async (snapshot) => {

                        const devoirStudents = await Promise.all(
                            snapshot.docs.map(async (devoirStudent) => {
                                const data = devoirStudent.data();

                                // Récupérer devoir
                                let devoirData = null;
                                if (data.devoirId) {
                                    const devoirSnap = await getDoc(
                                        doc(db, "devoirs", data.devoirId)
                                    );
                                    if (devoirSnap.exists()) {
                                        devoirData = {
                                            id: devoirSnap.id,
                                            ...devoirSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer matiere
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(
                                        doc(db, "matieres", data.matiereId)
                                    );
                                    if (matiereSnap.exists()) {
                                        matiereData = {
                                            id: matiereSnap.id,
                                            ...matiereSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer student
                                let studentData = null;
                                if (data.studentId) {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", data.studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        studentData = {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                }

                                return {
                                    id: devoirStudent.id,
                                    ...data,
                                    devoirData,
                                    studentData,
                                    matiereData,
                                };
                            })
                        );

                        updateCachedData(() => devoirStudents);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["devoirStudentsByUser"],
        }),
    }),
});

export const {
    useAddOrEditDevoirStudentMutation,
    useGetDevoirStudentsQuery,
    useGetDevoirStudentByProfQuery,
    useDeleteDevoirStudentMutation,
    useLazyGetDevoirStudentByIdQuery,
    useGetDevoirStudentsByUserQuery,
    useGetDevoirStudentsByDevoirQuery
} = DevoirStudentApi;

export function useDevoirStudentFromLocation(): [
    DevoirStudent,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<DevoirStudent | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetDevoirStudentByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as DevoirStudent);
        }
    }, [data]);

    return [item as DevoirStudent, isLoading, id as string, findById];
}